import createClient, { type Client, type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema.gen.ts";

export type { components, paths };
export type Me = components["schemas"]["Me"];
export type ApiVersion = components["schemas"]["ApiVersion"];
export type Problem = components["schemas"]["Problem"];

const SAFE = new Set(["GET", "HEAD", "OPTIONS", "TRACE"]);

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return undefined;
}

/**
 * CSRF для сессии BFF: сервер (Spring Security, CSRF spa) ставит cookie
 * XSRF-TOKEN, изменяющий запрос возвращает её заголовком X-XSRF-TOKEN.
 */
export function csrfHeaders(method: string): Record<string, string> {
  if (SAFE.has(method.toUpperCase())) return {};
  const token = readCookie("XSRF-TOKEN");
  return token ? { "X-XSRF-TOKEN": token } : {};
}

const csrf: Middleware = {
  onRequest({ request }) {
    for (const [k, v] of Object.entries(csrfHeaders(request.method))) request.headers.set(k, v);
    return request;
  },
};

export type ApiClient = Client<paths>;

/** Клиент API под сессионной cookie: тот же источник, что у страницы. */
export function createApi(baseUrl = ""): ApiClient {
  const client = createClient<paths>({ baseUrl, credentials: "same-origin" });
  client.use(csrf);
  return client;
}

/** Ответ сервера с ошибкой: problem+json с кодом правила, если он есть. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | undefined;

  constructor(status: number, problem?: Problem) {
    super(problem?.title ?? `HTTP ${String(status)}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

/**
 * Ответ openapi-fetch в данные или ApiError с problem+json. 204 и пустое
 * тело дают undefined.
 */
export async function unwrap<T>(request: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await request;
  if (!response.ok) throw new ApiError(response.status, error as Problem | undefined);
  return data as T;
}

/** Выход: сервер гасит сессию и отвечает 204. */
export async function logout(baseUrl = ""): Promise<void> {
  const res = await fetch(`${baseUrl}/logout`, {
    method: "POST",
    credentials: "same-origin",
    headers: csrfHeaders("POST"),
  });
  if (!res.ok && res.status !== 401) throw new ApiError(res.status);
}
